import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, ArrowLeft, DollarSign, Save, X, Upload, FileSpreadsheet, CheckCircle, AlertCircle, Package, Download, Calculator, CreditCard, TrendingUp, Smartphone, Building2, FileText, Receipt } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import projectBudgetService from '../services/projectBudgetService';
import projectsService from '../services/projectsService';
import projectFinanceService from '../services/projectFinanceService';
import financeService from '../services/financeService';
import inventoryService from '../services/inventoryService';
import surveyService from '../services/surveyService';
import materialPricingWeightsService from '../services/materialPricingWeightsService';
import paymentConditionsService from '../services/paymentConditionsService';
import cardRatesService from '../services/cardRatesService';
import CortesPdfToBudget from './CortesPdfToBudget';
import ConfirmModal from './ConfirmModal';
import toast from 'react-hot-toast';

const ProjectBudget = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [budgetItems, setBudgetItems] = useState([]);
  const [total, setTotal] = useState(0); // Total de venda (mantém compatibilidade)
  const [totalCost, setTotalCost] = useState(0); // Total de custo
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analysisItems, setAnalysisItems] = useState([]);
  const [uploadingExcel, setUploadingExcel] = useState(false);
  const [showAnalysis, setShowAnalysis] = useState(false);
  const [buildingBudget, setBuildingBudget] = useState(false);
  const [showBuildForm, setShowBuildForm] = useState(false);
  const [showCortesPdfModal, setShowCortesPdfModal] = useState(false);
  const [cardRates, setCardRates] = useState([]);
  const [extraProfitMargin, setExtraProfitMargin] = useState('');
  const [selectedPaymentConditions, setSelectedPaymentConditions] = useState([]);
  const [availablePaymentConditions, setAvailablePaymentConditions] = useState([]);
  const [showPaymentConditionsModal, setShowPaymentConditionsModal] = useState(false);
  
  // Condições de pagamento personalizadas
  const [hasCustomPayment, setHasCustomPayment] = useState(false);
  const [hasDownPayment, setHasDownPayment] = useState(false);
  const [downPaymentPercentage, setDownPaymentPercentage] = useState(0);
  const [downPaymentMethod, setDownPaymentMethod] = useState('');
  const [remainingPaymentMethod, setRemainingPaymentMethod] = useState('');
  const [clientDescription, setClientDescription] = useState('');
  const [budgetPdfs, setBudgetPdfs] = useState(null);
  const [expectedIncomes, setExpectedIncomes] = useState([]);
  const [payments, setPayments] = useState([]);
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [paymentFormData, setPaymentFormData] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_method_id: '',
    account_id: '',
    receiver_account_id: '',
    description: '',
    notes: '',
    expected_income_id: ''
  });
  const [formData, setFormData] = useState({
    item_name: '',
    category: '',
    description: '',
    quantity: 1,
    unit: 'un',
    unit_price: 0,
    unit_cost: 0 // Preço de custo
  });
  const [inventorySearch, setInventorySearch] = useState('');
  const [inventoryVariants, setInventoryVariants] = useState([]);
  const [loadingInventory, setLoadingInventory] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [materialWeightTotal, setMaterialWeightTotal] = useState(0);

  // Confirm modal states
  const [showBuildBudgetConfirm, setShowBuildBudgetConfirm] = useState(false);
  const [buildBudgetConfirmMessage, setBuildBudgetConfirmMessage] = useState('');
  const [showDeleteItemConfirm, setShowDeleteItemConfirm] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [showDeletePaymentConfirm, setShowDeletePaymentConfirm] = useState(false);
  const [deletePaymentId, setDeletePaymentId] = useState(null);

  useEffect(() => {
    loadProject();
    loadBudgetItems();
    loadAnalysis();
    // Verificar se há PDFs salvos no projeto (se já foi montado antes)
    checkExistingPDFs();
  }, [projectId]);

  useEffect(() => {
    // Carregar dados financeiros se projeto estiver aprovado
    if (project && ['PLANNING', 'IN_PROGRESS', 'PRODUCTION', 'ASSEMBLY', 'COMPLETED', 'DELIVERED'].includes(project.status)) {
      loadFinanceData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.status]);

  // Calcular pesos de preferências de material quando o projeto carregar
  useEffect(() => {
    if (project?.survey_id) {
      loadMaterialWeights();
    }
  }, [project?.survey_id]);

  const loadMaterialWeights = async () => {
    try {
      const surveyResponse = await surveyService.getSurveyById(project.survey_id);
      const survey = surveyResponse.data;
      
      if (survey.material_preferences) {
        const weightResponse = await materialPricingWeightsService.calculateTotalWeight(survey.material_preferences);
        setMaterialWeightTotal(weightResponse.total_weight || 0);
      } else {
        setMaterialWeightTotal(0);
      }
    } catch (error) {
      console.error('Erro ao calcular pesos de material:', error);
      // Não mostrar erro ao usuário, apenas não aplicar pesos
      setMaterialWeightTotal(0);
    }
  };

  // Carregar condições de pagamento disponíveis
  const loadPaymentConditions = async () => {
    try {
      const response = await paymentConditionsService.getAllConditions({ is_active: true });
      setAvailablePaymentConditions(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar condições de pagamento:', error);
    }
  };

  useEffect(() => {
    loadPaymentConditions();
  }, []);

  useEffect(() => {
    const loadCardRates = async () => {
      try {
        const response = await cardRatesService.getAllRates();
        setCardRates(response.data || []);
      } catch (error) {
        console.error('Erro ao carregar taxas de cartão:', error);
      }
    };
    loadCardRates();
  }, []);

  const getCardRate = (cardBrand, installments) => {
    if (!cardBrand || !installments || installments < 2) return 0;
    const rate = cardRates.find(
      r => r.card_brand?.toUpperCase() === cardBrand.toUpperCase() && r.installments === installments
    );
    return rate ? rate.rate_percentage / 100 : 0;
  };
  
  const loadFinanceData = async () => {
    try {
      const [expectedIncomesRes, paymentsRes] = await Promise.all([
        projectFinanceService.getExpectedIncomes(projectId),
        projectFinanceService.getPayments(projectId)
      ]);
      setExpectedIncomes(expectedIncomesRes.data || []);
      setPayments(paymentsRes.data || []);
      
      // Carregar métodos de pagamento e contas
      const [methodsRes, accountsRes] = await Promise.all([
        financeService.getPaymentMethods(),
        financeService.getAccounts()
      ]);
      setPaymentMethods(methodsRes.data || []);
      setAccounts(accountsRes.data || []);
    } catch (error) {
      console.error('Erro ao carregar dados financeiros:', error);
    }
  };

  const checkExistingPDFs = async () => {
    try {
      // Buscar projeto para verificar se tem PDFs gerados
      const response = await projectsService.getProjectById(projectId);
      if (response.data?.internal_notes) {
        // Tentar extrair informações de PDFs das notas internas
        // Por enquanto, vamos apenas verificar se o projeto tem orçamento montado
        // e tentar buscar PDFs mais recentes
        if (response.data.status === 'AWAITING_CLIENT' && response.data.estimated_budget) {
          // Projeto já tem orçamento montado, mas não temos como saber os nomes dos arquivos
          // Por enquanto, deixamos vazio - os PDFs só aparecerão após nova geração
        }
      }
    } catch (error) {
      console.error('Erro ao verificar PDFs existentes:', error);
    }
  };

  const loadProject = async () => {
    try {
      const response = await projectsService.getProjectById(projectId);
      setProject(response.data);
    } catch (error) {
      console.error('Erro ao carregar projeto:', error);
      toast.error('Erro ao carregar projeto');
    }
  };

  const loadBudgetItems = async () => {
    try {
      setLoading(true);
      const response = await projectBudgetService.getBudgetItems(projectId);
      setBudgetItems(response.data || []);
      const totalPrice = response.total_price || response.total || 0;
      const totalCostValue = response.total_cost || 0;
      console.log('📊 Totais carregados:', { totalPrice, totalCostValue, response });
      setTotal(totalPrice); // Total de venda
      setTotalCost(totalCostValue); // Total de custo
    } catch (error) {
      console.error('Erro ao carregar itens de orçamento:', error);
      toast.error('Erro ao carregar itens de orçamento');
    } finally {
      setLoading(false);
    }
  };

  const loadAnalysis = async () => {
    try {
      const response = await projectBudgetService.getAnalysis(projectId);
      if (response.data) {
        setAnalysis(response.data.analysis);
        setAnalysisItems(response.data.items || []);
      }
    } catch (error) {
      console.error('Erro ao carregar análise:', error);
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls|csv)$/i)) {
      toast.error('Apenas arquivos Excel (.xlsx, .xls) ou CSV são permitidos');
      return;
    }

    try {
      setUploadingExcel(true);
      const response = await projectBudgetService.uploadExcelAnalysis(projectId, file);
      toast.success('Análise processada com sucesso!');
      setAnalysis(response.data.analysis);
      setAnalysisItems(response.data.items || []);
      setShowAnalysis(true);
    } catch (error) {
      console.error('Erro ao processar Excel:', error);
      toast.error(error.response?.data?.message || 'Erro ao processar arquivo Excel');
    } finally {
      setUploadingExcel(false);
      e.target.value = ''; // Reset input
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      // Usar o mesmo baseURL do api service
      const apiBaseUrl = process.env.REACT_APP_API_URL || '/api';
      const token = localStorage.getItem('accessToken');
      
      // Se baseURL não começar com http, assumir que é relativo
      const fullUrl = apiBaseUrl.startsWith('http') 
        ? `${apiBaseUrl}/projects/budget/template`
        : `${apiBaseUrl}/projects/budget/template`;
      
      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Erro ao baixar template');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'template-orcamento-projeto.xlsx';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Template baixado com sucesso!');
    } catch (error) {
      console.error('Erro ao baixar template:', error);
      toast.error(error.message || 'Erro ao baixar template Excel');
    }
  };

  const handleBuildBudget = async () => {
    if (!showBuildForm) {
      setShowBuildForm(true);
      return;
    }

    if (!clientDescription || !clientDescription.trim()) {
      toast.error('Por favor, preencha as informações do projeto para o cliente');
      return;
    }

    const extraMargin = parseFloat(extraProfitMargin) || 0;
    if (extraProfitMargin && (extraMargin < 0 || extraMargin > 1000)) {
      toast.error('Margem de lucro extra deve estar entre 0% e 1000%');
      return;
    }

    const confirmMessage = extraMargin > 0
      ? `Tem certeza que deseja montar o orçamento com ${extraMargin}% de margem extra? O projeto será enviado para "Aguardando Cliente".`
      : 'Tem certeza que deseja montar o orçamento? O projeto será enviado para "Aguardando Cliente".';

    setBuildBudgetConfirmMessage(confirmMessage);
    setShowBuildBudgetConfirm(true);
  };

  const confirmBuildBudget = async () => {
    setShowBuildBudgetConfirm(false);

    const extraMargin = parseFloat(extraProfitMargin) || 0;

    try {
      setBuildingBudget(true);
      const customPaymentConditions = hasCustomPayment ? {
        has_down_payment: hasDownPayment,
        down_payment_percentage: hasDownPayment ? parseFloat(downPaymentPercentage) : 0,
        down_payment_method_id: hasDownPayment && downPaymentMethod ? parseInt(downPaymentMethod) : null,
        remaining_payment_method_id: remainingPaymentMethod ? parseInt(remainingPaymentMethod) : null
      } : null;

      const response = await projectBudgetService.buildBudget(projectId, {
        extra_profit_margin: extraMargin,
        client_description: clientDescription,
        selected_payment_conditions: selectedPaymentConditions,
        custom_payment_conditions: customPaymentConditions
      });

      toast.success(response.message || 'Orçamento montado com sucesso!', {
        duration: 5000
      });

      await loadProject();
      await loadBudgetItems();
      await loadAnalysis();

      if (response.data?.pdfs && response.data.pdfs.manager && response.data.pdfs.client) {
        setBudgetPdfs(response.data.pdfs);
      } else {
        toast.error('Orçamento montado, mas PDFs não foram gerados. Verifique os logs do servidor.');
      }

      if (response.data) {
        const summaryParts = [
          `Custo Total: ${projectBudgetService.formatCurrency(response.data.total_cost)}`
        ];

        if (response.data.material_weight_total && response.data.material_weight_total !== 0) {
          summaryParts.push(`Pesos de Material: ${response.data.material_weight_total > 0 ? '+' : ''}${response.data.material_weight_total}%`);
          summaryParts.push(`Valor com Pesos: ${projectBudgetService.formatCurrency(response.data.budget_with_weights)}`);
        }

        if (response.data.extra_profit_margin && response.data.extra_profit_margin > 0) {
          summaryParts.push(`Margem Extra: ${response.data.extra_profit_margin}%`);
        }

        summaryParts.push(`Valor Final: ${projectBudgetService.formatCurrency(response.data.final_budget)}`);

        toast.success(summaryParts.join('\n'), {
          duration: 6000,
          style: { whiteSpace: 'pre-line' }
        });
      }

      setShowBuildForm(false);
    } catch (error) {
      console.error('Erro ao montar orçamento:', error);
      toast.error(error.response?.data?.message || 'Erro ao montar orçamento');
    } finally {
      setBuildingBudget(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Garantir que unit_cost seja enviado corretamente
      // Se unit_cost for 0 ou não definido, usar unit_price como fallback (para itens manuais)
      const submitData = {
        ...formData,
        unit_cost: (formData.unit_cost && parseFloat(formData.unit_cost) > 0) 
          ? parseFloat(formData.unit_cost) 
          : (formData.unit_price || 0)
      };
      console.log('💾 Salvando item:', {
        ...submitData,
        unit_cost_value: submitData.unit_cost,
        unit_price_value: submitData.unit_price,
        has_unit_cost: !!formData.unit_cost && parseFloat(formData.unit_cost) > 0
      });
      
      if (editingItem) {
        await projectBudgetService.updateBudgetItem(projectId, editingItem.id, submitData);
        toast.success('Item atualizado com sucesso!');
      } else {
        await projectBudgetService.createBudgetItem(projectId, submitData);
        toast.success('Item adicionado com sucesso!');
      }
      
      resetForm();
      loadBudgetItems();
    } catch (error) {
      console.error('Erro ao salvar item:', error);
      toast.error(error.response?.data?.message || 'Erro ao salvar item');
    }
  };

  const handleDelete = async (itemId) => {
    setDeleteItemId(itemId);
    setShowDeleteItemConfirm(true);
  };

  const confirmDeleteItem = async () => {
    setShowDeleteItemConfirm(false);
    if (!deleteItemId) return;

    try {
      await projectBudgetService.deleteBudgetItem(projectId, deleteItemId);
      toast.success('Item excluído com sucesso!');
      loadBudgetItems();
    } catch (error) {
      console.error('Erro ao excluir item:', error);
      toast.error('Erro ao excluir item');
    }
  };

  const confirmDeletePayment = async () => {
    setShowDeletePaymentConfirm(false);
    if (!deletePaymentId) return;

    try {
      await projectFinanceService.deletePayment(projectId, deletePaymentId);
      toast.success('Pagamento excluído com sucesso!');
      await loadFinanceData();
    } catch (error) {
      console.error('Erro ao excluir pagamento:', error);
      toast.error('Erro ao excluir pagamento');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      item_name: item.item_name,
      category: item.category || '',
      description: item.description || '',
      quantity: item.quantity,
      unit: item.unit,
      unit_price: item.unit_price,
      unit_cost: item.unit_cost || item.unit_price // Se não tiver unit_cost, usa unit_price
    });
    setShowForm(true);
  };

  const resetForm = () => {
    setFormData({
      item_name: '',
      category: '',
      description: '',
      quantity: 1,
      unit: 'un',
      unit_price: 0,
      unit_cost: 0
    });
    setEditingItem(null);
    setShowForm(false);
    setInventorySearch('');
    setInventoryVariants([]);
    setSelectedVariant(null);
  };

  // Buscar variantes do estoque
  useEffect(() => {
    if (showForm && !editingItem && inventorySearch.length >= 2) {
      const timeoutId = setTimeout(() => {
        loadInventoryVariants();
      }, 500);
      return () => clearTimeout(timeoutId);
    } else if (inventorySearch.length < 2) {
      setInventoryVariants([]);
    }
  }, [inventorySearch, showForm, editingItem]);

  const loadInventoryVariants = async () => {
    try {
      setLoadingInventory(true);
      const response = await inventoryService.getVariantsForBudget({
        search: inventorySearch,
        ativo: true
      });
      setInventoryVariants(response.data || []);
    } catch (error) {
      console.error('Erro ao buscar variantes:', error);
      toast.error('Erro ao buscar itens do estoque');
    } finally {
      setLoadingInventory(false);
    }
  };

  const handleSelectVariant = (variant) => {
    setSelectedVariant(variant);
    setFormData({
      ...formData,
      item_name: `${variant.produto_nome}${variant.cor ? ` - ${variant.cor}` : ''}${variant.tamanho_label ? ` (${variant.tamanho_label})` : ''}`,
      unit_price: parseFloat(variant.preco_venda) || 0,
      unit_cost: parseFloat(variant.preco_custo) || 0,
      unit: variant.unidade_sigla || 'un',
      description: variant.medida_text || variant.produto_nome || '',
      variant_id: variant.id,
      warehouse_id: variant.warehouse_id || null
    });
    setInventorySearch('');
    setInventoryVariants([]);
  };

  const categories = [
    { value: 'material', label: 'Material' },
    { value: 'mão_de_obra', label: 'Mão de Obra' },
    { value: 'serviço', label: 'Serviço' },
    { value: 'outro', label: 'Outro' }
  ];

  const units = [
    { value: 'un', label: 'Unidade (un)' },
    { value: 'm²', label: 'Metro quadrado (m²)' },
    { value: 'm', label: 'Metro (m)' },
    { value: 'kg', label: 'Quilograma (kg)' },
    { value: 'lt', label: 'Litro (lt)' }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Carregando...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/projetos')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Voltar para Projetos
        </button>
        
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Orçamento do Projeto
            </h1>
            {project && (
              <div>
                <p className="text-gray-600 mt-1">
                  {project.project_code} - {project.project_name}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${projectsService.getStatusColor(project.status)}`}>
                    {projectsService.formatStatus(project.status)}
                  </span>
                  {project.estimated_budget && (
                    <span className="text-sm text-gray-600">
                      Orçamento: {projectsService.formatCurrency(project.estimated_budget)}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          
          <div className="flex gap-2">
            {!showForm && (
              <>
                <button
                  onClick={handleDownloadTemplate}
                  className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                  title="Baixar template Excel padrão"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Baixar Template
                </button>
                <label className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 cursor-pointer">
                  <Upload className="w-4 h-4 mr-2" />
                  {uploadingExcel ? 'Processando...' : 'Analisar Excel'}
                  <input
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleExcelUpload}
                    className="hidden"
                    disabled={uploadingExcel}
                  />
                </label>
                <button
                  onClick={() => setShowCortesPdfModal(true)}
                  className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                  title="Importar plano de corte PDF para orçamento"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Importar Cortes PDF
                </button>
                <button
                  onClick={() => setShowForm(true)}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar Item
                </button>
                <button
                  onClick={handleBuildBudget}
                  disabled={buildingBudget || (total === 0 && (!analysis || analysis.total_price === 0))}
                  className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                  title="Montar orçamento final (soma análise + itens manuais) e enviar para aguardando cliente"
                >
                  <Calculator className="w-4 h-4 mr-2" />
                  {buildingBudget ? 'Montando...' : 'Montar Orçamento'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Formulário de Montar Orçamento */}
      {showBuildForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6 border-2 border-purple-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-purple-900">Montar Orçamento Final</h2>
            <button
              onClick={() => setShowBuildForm(false)}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Informações do Projeto para o Cliente *
              </label>
              <textarea
                value={clientDescription}
                onChange={(e) => setClientDescription(e.target.value)}
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                placeholder="Descreva o projeto de forma clara e profissional para o cliente. Estas informações aparecerão no PDF do orçamento enviado ao cliente."
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                * Este texto será exibido no PDF do cliente. Seja claro e profissional.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Margem de Lucro Extra (%)
              </label>
              <input
                type="number"
                min="0"
                max="1000"
                step="0.1"
                value={extraProfitMargin}
                onChange={(e) => setExtraProfitMargin(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                placeholder="Ex: 10 para 10% (opcional)"
              />
              <p className="text-xs text-gray-500 mt-1">
                Margem adicional opcional para casos especiais. Deixe em branco se não houver necessidade.
                Será aplicada sobre o valor já ajustado pelos pesos de preferências de material.
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Condições de Pagamento Sugeridas
                </label>
                <p className="text-xs text-gray-500 mb-3">
                  Selecione as condições de pagamento que serão oferecidas ao cliente no orçamento.
                </p>
                
                {/* Botão para abrir seleção */}
                <button
                  type="button"
                  onClick={() => {
                    // Recarregar condições antes de abrir
                    loadPaymentConditions();
                    setShowPaymentConditionsModal(true);
                  }}
                  className="w-full px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-purple-500 hover:bg-purple-50 transition-colors flex items-center justify-center gap-2 text-gray-700"
                >
                  <CreditCard className="w-5 h-5" />
                  <span className="font-medium">
                    {selectedPaymentConditions.length > 0 
                      ? `${selectedPaymentConditions.length} condição(ões) selecionada(s)` 
                      : 'Selecionar Condições de Pagamento'}
                  </span>
                </button>

                {/* Condições selecionadas (resumo) */}
                {selectedPaymentConditions.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {availablePaymentConditions
                      .filter(c => selectedPaymentConditions.includes(c.id))
                      .map((condition) => (
                        <div
                          key={condition.id}
                          className="flex items-center justify-between p-2 bg-purple-50 border border-purple-200 rounded-lg"
                        >
                          <div className="flex-1">
                            <div className="text-sm font-medium text-gray-900">{condition.name}</div>
                            {condition.affects_price && (
                              <div className="text-xs text-blue-600 mt-1">
                                {condition.discount_percentage > 0 && `Desconto: ${condition.discount_percentage}%`}
                                {condition.increase_percentage > 0 && `Acréscimo: ${condition.increase_percentage}%`}
                                {condition.installments > 1 && (() => {
                                  // Calcular valor por parcela
                                  const baseCost = total + (analysis?.total_cost || 0);
                                  const weightMultiplier = materialWeightTotal !== 0 ? 1 + (materialWeightTotal / 100) : 1;
                                  const costWithWeights = baseCost * weightMultiplier;
                                  const extraMargin = parseFloat(extraProfitMargin) || 0;
                                  const extraMarginMultiplier = extraMargin > 0 ? 1 + (extraMargin / 100) : 1;
                                  let finalValue = costWithWeights * extraMarginMultiplier;
                                  
                                  // Aplicar desconto/acréscimo da condição se afetar o preço
                                  if (condition.affects_price) {
                                    if (condition.discount_percentage > 0) {
                                      finalValue = finalValue * (1 - condition.discount_percentage / 100);
                                    } else if (condition.increase_percentage > 0) {
                                      finalValue = finalValue * (1 + condition.increase_percentage / 100);
                                    }
                                  }
                                  
                                  // Aplicar taxa de cartão se houver
                                  const cardRate = getCardRate(condition.card_brand, condition.installments);
                                  if (cardRate > 0 && condition.rate_paid_by === 'cliente') {
                                    finalValue = finalValue * (1 + cardRate);
                                  }

                                  const installmentValue = finalValue / condition.installments;

                                  const rateLabel = cardRate > 0 && condition.rate_paid_by === 'cliente' ? 'com taxa' : 'sem juros';
                                  return ` • ${condition.installments}x de ${projectBudgetService.formatCurrency(installmentValue)} (${rateLabel})`;
                                })()}
                              </div>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedPaymentConditions(selectedPaymentConditions.filter(id => id !== condition.id));
                            }}
                            className="text-red-600 hover:text-red-800 ml-2"
                            title="Remover"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                  </div>
                )}

                {availablePaymentConditions.length === 0 && (
                  <div className="mt-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center">
                    <p className="text-sm text-yellow-800 mb-2">
                      Nenhuma condição de pagamento cadastrada.
                    </p>
                    <a 
                      href="/condicoes-pagamento" 
                      className="text-sm text-blue-600 hover:underline font-medium"
                    >
                      Cadastrar condições de pagamento
                    </a>
                  </div>
                )}
              </div>

              {/* Condições de Pagamento Personalizadas */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center gap-2 mb-3">
                  <input
                    type="checkbox"
                    id="hasCustomPayment"
                    checked={hasCustomPayment}
                    onChange={(e) => {
                      setHasCustomPayment(e.target.checked);
                      if (!e.target.checked) {
                        setHasDownPayment(false);
                        setDownPaymentPercentage(0);
                        setDownPaymentMethod('');
                        setRemainingPaymentMethod('');
                      }
                    }}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="hasCustomPayment" className="text-sm font-medium text-gray-700 cursor-pointer">
                    Usar condições de pagamento personalizadas
                  </label>
                </div>
                
                {hasCustomPayment && (
                  <div className="ml-6 space-y-4 bg-gray-50 p-4 rounded-lg">
                    {/* Entrada */}
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <input
                          type="checkbox"
                          id="hasDownPayment"
                          checked={hasDownPayment}
                          onChange={(e) => {
                            setHasDownPayment(e.target.checked);
                            if (!e.target.checked) {
                              setDownPaymentPercentage(0);
                              setDownPaymentMethod('');
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="hasDownPayment" className="text-sm font-medium text-gray-700 cursor-pointer">
                          Terá entrada?
                        </label>
                      </div>
                      
                      {hasDownPayment && (
                        <div className="ml-6 space-y-3 mt-3">
                          <div>
                            <label className="block text-sm text-gray-600 mb-1">
                              Porcentagem da Entrada (%)
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.01"
                              value={downPaymentPercentage}
                              onChange={(e) => setDownPaymentPercentage(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="Ex: 30"
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm text-gray-600 mb-1">
                              Como será paga a entrada?
                            </label>
                            <select
                              value={downPaymentMethod}
                              onChange={(e) => setDownPaymentMethod(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                              <option value="">Selecione uma modalidade</option>
                              {availablePaymentConditions.map((condition) => (
                                <option key={condition.id} value={condition.id}>
                                  {condition.name}
                                  {condition.affects_price && condition.discount_percentage > 0 && ` (Desconto: ${condition.discount_percentage}%)`}
                                  {condition.affects_price && condition.increase_percentage > 0 && ` (Acréscimo: ${condition.increase_percentage}%)`}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Restante */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Como será pago o restante?
                      </label>
                      <select
                        value={remainingPaymentMethod}
                        onChange={(e) => setRemainingPaymentMethod(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Selecione uma modalidade</option>
                        {availablePaymentConditions.map((condition) => (
                          <option key={condition.id} value={condition.id}>
                            {condition.name}
                            {condition.affects_price && condition.discount_percentage > 0 && ` (Desconto: ${condition.discount_percentage}%)`}
                            {condition.affects_price && condition.increase_percentage > 0 && ` (Acréscimo: ${condition.increase_percentage}%)`}
                            {condition.installments > 1 && ` - ${condition.installments}x`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="bg-purple-50 p-4 rounded-lg space-y-2">
              <p className="text-sm text-gray-700 mb-2">
                <strong>Resumo do Orçamento:</strong>
              </p>
              
              {(() => {
                const costPrice = totalCost + (analysis?.total_cost || 0); // Preço de custo: soma de todos os itens (custo)
                const baseValue = total + (analysis?.total_price || 0); // Valor base: soma de todos os itens (venda)
                const weightMultiplier = materialWeightTotal !== 0 ? 1 + (materialWeightTotal / 100) : 1;
                const costWithWeights = baseValue * weightMultiplier; // Aplica pesos no valor de venda
                const extraMargin = parseFloat(extraProfitMargin) || 0;
                const extraMarginMultiplier = extraMargin > 0 ? 1 + (extraMargin / 100) : 1;
                const finalValue = costWithWeights * extraMarginMultiplier;
                
                return (
                  <>
                    <p className="text-sm text-gray-600">
                      Preço de custo: {projectBudgetService.formatCurrency(costPrice)}
                    </p>
                    <p className="text-sm text-gray-600">
                      Valor base: {projectBudgetService.formatCurrency(baseValue)}
                    </p>
                    {materialWeightTotal !== 0 && (
                      <>
                        <p className="text-sm text-blue-600">
                          Pesos de Material: {materialWeightTotal > 0 ? '+' : ''}{materialWeightTotal}%
                        </p>
                        <p className="text-sm text-gray-600">
                          Valor com pesos: {projectBudgetService.formatCurrency(costWithWeights)}
                        </p>
                      </>
                    )}
                    {extraMargin > 0 && (
                      <p className="text-sm text-orange-600">
                        Margem extra: {extraMargin}%
                      </p>
                    )}
                    <p className="text-sm font-semibold text-purple-900 border-t border-purple-200 pt-2 mt-2">
                      Valor final: {projectBudgetService.formatCurrency(finalValue)}
                    </p>
                  </>
                );
              })()}
            </div>

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowBuildForm(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleBuildBudget}
                disabled={buildingBudget}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-400"
              >
                {buildingBudget ? 'Montando...' : 'Confirmar e Montar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Seleção de Condições de Pagamento */}
      {showPaymentConditionsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Selecionar Condições de Pagamento</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Escolha as condições que serão oferecidas ao cliente no orçamento
                </p>
              </div>
              <button
                onClick={() => setShowPaymentConditionsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(80vh-180px)]">
              {availablePaymentConditions.length === 0 ? (
                <div className="text-center py-8">
                  <CreditCard className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-4">Nenhuma condição de pagamento cadastrada.</p>
                  <a 
                    href="/condicoes-pagamento" 
                    className="inline-block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  >
                    Cadastrar Condições
                  </a>
                </div>
              ) : (
                <div className="space-y-2">
                  {availablePaymentConditions.map((condition) => {
                    const isSelected = selectedPaymentConditions.includes(condition.id);
                    const TypeIcon = {
                      cash: DollarSign,
                      pix: Smartphone,
                      credit_card: CreditCard,
                      debit_card: CreditCard,
                      bank_transfer: Building2,
                      boleto: FileText,
                      cheque: Receipt,
                      custom: DollarSign
                    }[condition.payment_type] || DollarSign;
                    
                    return (
                      <label
                        key={condition.id}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedPaymentConditions(selectedPaymentConditions.filter(id => id !== condition.id));
                          } else {
                            setSelectedPaymentConditions([...selectedPaymentConditions, condition.id]);
                          }
                        }}
                        className={`flex items-start gap-3 p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          isSelected
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex-shrink-0 mt-1">
                          {isSelected ? (
                            <div className="w-5 h-5 rounded-full bg-purple-600 flex items-center justify-center">
                              <CheckCircle className="w-3 h-3 text-white" />
                            </div>
                          ) : (
                            <div className="w-5 h-5 rounded-full border-2 border-gray-300"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <TypeIcon className="w-5 h-5 text-gray-400" />
                            <div className="text-base font-medium text-gray-900">{condition.name}</div>
                          </div>
                          {condition.description && (
                            <div className="text-sm text-gray-500 mb-2">{condition.description}</div>
                          )}
                          <div className="flex flex-wrap gap-2 mt-2">
                            {condition.affects_price && (
                              <>
                                {condition.discount_percentage > 0 && (
                                  <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded">
                                    Desconto: {condition.discount_percentage}%
                                  </span>
                                )}
                                {condition.increase_percentage > 0 && (
                                  <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                                    Acréscimo: {condition.increase_percentage}%
                                  </span>
                                )}
                              </>
                            )}
                            {!condition.affects_price && (
                              <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                                Não afeta o preço
                              </span>
                            )}
                            {condition.payment_type === 'credit_card' && condition.installments > 1 && (() => {
                              // Calcular valor por parcela
                              const baseCost = total + (analysis?.total_cost || 0);
                              const weightMultiplier = materialWeightTotal !== 0 ? 1 + (materialWeightTotal / 100) : 1;
                              const costWithWeights = baseCost * weightMultiplier;
                              const extraMargin = parseFloat(extraProfitMargin) || 0;
                              const extraMarginMultiplier = extraMargin > 0 ? 1 + (extraMargin / 100) : 1;
                              let finalValue = costWithWeights * extraMarginMultiplier;
                              
                              // Aplicar desconto/acréscimo da condição se afetar o preço
                              if (condition.affects_price) {
                                if (condition.discount_percentage > 0) {
                                  finalValue = finalValue * (1 - condition.discount_percentage / 100);
                                } else if (condition.increase_percentage > 0) {
                                  finalValue = finalValue * (1 + condition.increase_percentage / 100);
                                }
                              }
                              
                              // Aplicar taxa de cartão se houver
                              const cardRate = getCardRate(condition.card_brand, condition.installments);
                              if (cardRate > 0 && condition.rate_paid_by === 'cliente') {
                                finalValue = finalValue * (1 + cardRate);
                              }

                              const installmentValue = finalValue / condition.installments;

                              const rateLabel = cardRate > 0 && condition.rate_paid_by === 'cliente' ? 'com taxa' : 'sem juros';
                              return (
                                <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                                  {condition.installments}x de {projectBudgetService.formatCurrency(installmentValue)} ({rateLabel})
                                </span>
                              );
                            })()}
                            {(() => {
                              const cardRate = getCardRate(condition.card_brand, condition.installments);
                              return cardRate > 0 && condition.rate_paid_by === 'cliente' ? (
                                <span className="px-2 py-1 text-xs font-medium bg-orange-100 text-orange-800 rounded">
                                  Taxa: {(cardRate * 100).toFixed(2)}%
                                </span>
                              ) : null;
                            })()}
                            {condition.card_brand && condition.card_brand !== 'all' && (
                              <span className="px-2 py-1 text-xs font-medium bg-indigo-100 text-indigo-800 rounded">
                                {condition.card_brand.toUpperCase()}
                              </span>
                            )}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {selectedPaymentConditions.length > 0 && (
                  <span className="font-medium text-purple-600">
                    {selectedPaymentConditions.length} condição(ões) selecionada(s)
                  </span>
                )}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowPaymentConditionsModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={() => setShowPaymentConditionsModal(false)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Confirmar Seleção
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Botões de Download de PDFs */}
      {budgetPdfs && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-green-900 mb-2">PDFs Gerados com Sucesso!</h3>
              <p className="text-sm text-green-700">Baixe os arquivos de orçamento:</p>
            </div>
            <div className="flex gap-2">
              {budgetPdfs.manager && (
                <button
                  onClick={async () => {
                    try {
                      const urlParts = budgetPdfs.manager.split('file=');
                      const filename = urlParts.length > 1 ? urlParts[1] : budgetPdfs.manager.split('/').pop();
                      console.log('📥 Baixando PDF gerente:', filename);
                      const blob = await projectBudgetService.downloadManagerPDF(projectId, filename);
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `orcamento-gerente-${projectId}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                      toast.success('PDF do gerente baixado com sucesso!');
                    } catch (error) {
                      console.error('Erro ao baixar PDF do gerente:', error);
                      toast.error('Erro ao baixar PDF do gerente');
                    }
                  }}
                  className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  PDF Gerente
                </button>
              )}
              {budgetPdfs.client && (
                <button
                  onClick={async () => {
                    try {
                      const urlParts = budgetPdfs.client.split('file=');
                      const filename = urlParts.length > 1 ? urlParts[1] : budgetPdfs.client.split('/').pop();
                      console.log('📥 Baixando PDF cliente:', filename);
                      const blob = await projectBudgetService.downloadClientPDF(projectId, filename);
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `orcamento-cliente-${projectId}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                      toast.success('PDF do cliente baixado com sucesso!');
                    } catch (error) {
                      console.error('Erro ao baixar PDF do cliente:', error);
                      toast.error('Erro ao baixar PDF do cliente');
                    }
                  }}
                  className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  PDF Cliente
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Análise Inteligente */}
      {analysis && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <FileSpreadsheet className="w-6 h-6 text-green-600" />
              <div>
                <h2 className="text-lg font-semibold">Análise Inteligente de Orçamento</h2>
                <p className="text-sm text-gray-500">
                  Arquivo: {analysis.excel_file_name} • 
                  Processado em: {new Date(analysis.processed_at).toLocaleString('pt-BR')}
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowAnalysis(!showAnalysis)}
              className="text-gray-500 hover:text-gray-700"
            >
              {showAnalysis ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>

          {showAnalysis && (
            <>
              {/* Resumo da Análise */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Total de Itens</div>
                  <div className="text-2xl font-bold text-blue-600">{analysis.total_items}</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Do Estoque</div>
                  <div className="text-2xl font-bold text-green-600">
                    {projectBudgetService.formatCurrency(analysis.total_from_stock)}
                  </div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Para Comprar</div>
                  <div className="text-2xl font-bold text-orange-600">
                    {projectBudgetService.formatCurrency(analysis.total_to_buy)}
                  </div>
                </div>
                <div className="bg-purple-50 p-4 rounded-lg">
                  <div className="text-sm text-gray-600">Preço de Repasse</div>
                  <div className="text-2xl font-bold text-purple-600">
                    {projectBudgetService.formatCurrency(analysis.total_price)}
                  </div>
                </div>
              </div>

              {/* Tabela de Itens da Análise */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left">Item</th>
                      <th className="px-4 py-2 text-left">Qtd. Necessária</th>
                      <th className="px-4 py-2 text-left">Estoque Disponível</th>
                      <th className="px-4 py-2 text-left">Do Estoque</th>
                      <th className="px-4 py-2 text-left">Para Comprar</th>
                      <th className="px-4 py-2 text-right">Custo Total</th>
                      <th className="px-4 py-2 text-right">Preço Repasse</th>
                      <th className="px-4 py-2 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {analysisItems.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2">
                          <div className="font-medium">{item.item_name}</div>
                          {item.product_name && (
                            <div className="text-xs text-gray-500">
                              {item.product_name} {item.cor ? `- ${item.cor}` : ''}
                            </div>
                          )}
                          {item.status === 'NOT_FOUND' && (
                            <div className="text-xs text-red-500 mt-1">
                              Produto não encontrado no estoque
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2">{item.quantity_required} {item.unit}</td>
                        <td className="px-4 py-2">
                          {item.stock_available > 0 ? (
                            <span className="text-green-600 font-medium">{item.stock_available}</span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {item.stock_used > 0 ? (
                            <span className="text-green-600 font-medium">{item.stock_used}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-2">
                          {item.quantity_to_buy > 0 ? (
                            <span className="text-orange-600 font-medium">{item.quantity_to_buy}</span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">
                          {projectBudgetService.formatCurrency(item.total_cost)}
                        </td>
                        <td className="px-4 py-2 text-right font-semibold">
                          {projectBudgetService.formatCurrency(item.total_price)}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {item.status === 'MATCHED' ? (
                            <CheckCircle className="w-5 h-5 text-green-600 mx-auto" />
                          ) : (
                            <AlertCircle className="w-5 h-5 text-orange-600 mx-auto" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-gray-50 font-bold">
                    <tr>
                      <td colSpan="5" className="px-4 py-2 text-right">Total:</td>
                      <td className="px-4 py-2 text-right">
                        {projectBudgetService.formatCurrency(analysis.total_cost)}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {projectBudgetService.formatCurrency(analysis.total_price)}
                      </td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Formulário de adicionar/editar */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">
              {editingItem ? 'Editar Item' : 'Novo Item'}
            </h2>
            <button
              onClick={resetForm}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {!editingItem && (
              <div className="mb-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Buscar Item no Estoque
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={inventorySearch}
                    onChange={(e) => setInventorySearch(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Digite o nome, SKU ou código do produto..."
                  />
                  {loadingInventory && (
                    <div className="absolute right-3 top-2.5">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                </div>
                {inventoryVariants.length > 0 && (
                  <div className="mt-2 max-h-60 overflow-y-auto border border-gray-200 rounded-lg bg-white">
                    {inventoryVariants.map((variant) => (
                      <div
                        key={variant.variant_id}
                        onClick={() => handleSelectVariant(variant)}
                        className="p-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-900">
                              {variant.produto_nome}
                            </div>
                            
                            {/* Informações da Variante */}
                            <div className="mt-1 space-y-0.5">
                              {/* Cor */}
                              {variant.cor && (
                                <div className="text-sm text-gray-700">
                                  <span className="font-medium">Cor:</span> {variant.cor}
                                </div>
                              )}
                              
                              {/* Acabamento */}
                              {variant.acabamento && (
                                <div className="text-sm text-gray-700">
                                  <span className="font-medium">Acabamento:</span> {variant.acabamento}
                                </div>
                              )}
                              
                              {/* Dimensões */}
                              {(variant.espessura_mm || variant.comprimento_cm || variant.largura_cm || variant.tamanho_label || variant.medida_text) && (
                                <div className="text-sm text-gray-700">
                                  <span className="font-medium">Dimensões:</span>{' '}
                                  {variant.espessura_mm && `${variant.espessura_mm}mm`}
                                  {variant.espessura_mm && (variant.comprimento_cm || variant.largura_cm) && ' × '}
                                  {variant.comprimento_cm && `${variant.comprimento_cm}cm`}
                                  {variant.comprimento_cm && variant.largura_cm && ' × '}
                                  {variant.largura_cm && `${variant.largura_cm}cm`}
                                  {!variant.espessura_mm && !variant.comprimento_cm && !variant.largura_cm && variant.tamanho_label && variant.tamanho_label}
                                  {!variant.espessura_mm && !variant.comprimento_cm && !variant.largura_cm && !variant.tamanho_label && variant.medida_text && variant.medida_text}
                                </div>
                              )}
                              
                              {/* SKU e Códigos */}
                              <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-2">
                                {variant.sku_variacao && (
                                  <span>SKU: <span className="font-medium">{variant.sku_variacao}</span></span>
                                )}
                                {variant.produto_sku && (
                                  <span>Produto SKU: <span className="font-medium">{variant.produto_sku}</span></span>
                                )}
                                {variant.codigo_interno && (
                                  <span>Código: <span className="font-medium">{variant.codigo_interno}</span></span>
                                )}
                              </div>
                              
                              {/* Categoria e Estoque */}
                              <div className="text-xs text-gray-500 mt-1 flex items-center gap-2">
                                {variant.categoria_nome && (
                                  <span className="px-2 py-0.5 bg-gray-100 rounded">
                                    {variant.categoria_nome}
                                  </span>
                                )}
                                <span className={`px-2 py-0.5 rounded ${
                                  variant.estoque_disponivel > 0 
                                    ? 'bg-green-100 text-green-700' 
                                    : 'bg-orange-100 text-orange-700'
                                }`}>
                                  Estoque: {variant.estoque_disponivel || 0} {variant.unidade_sigla || 'un'}
                                </span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="text-right ml-4 flex-shrink-0">
                            <div className="font-semibold text-blue-600 text-lg">
                              {projectBudgetService.formatCurrency(variant.preco_venda || 0)}
                            </div>
                            {variant.preco_custo && variant.preco_custo > 0 && (
                              <div className="text-xs text-gray-500 mt-1">
                                Custo: {projectBudgetService.formatCurrency(variant.preco_custo)}
                              </div>
                            )}
                            {variant.estoque_disponivel === 0 && (
                              <div className="text-xs text-orange-600 mt-1 font-medium">Estoque zerado</div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {selectedVariant && (
                  <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded text-sm text-green-800">
                    ✓ Item selecionado: {formData.item_name}
                  </div>
                )}
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome do Item *
                </label>
                <input
                  type="text"
                  required
                  value={formData.item_name}
                  onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Ex: MDF 18mm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Categoria
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Selecione...</option>
                  {categories.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Quantidade
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.quantity}
                  onChange={(e) => setFormData({ ...formData, quantity: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Unidade
                </label>
                <select
                  value={formData.unit}
                  onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {units.map(unit => (
                    <option key={unit.value} value={unit.value}>{unit.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preço de Custo Unitário
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.unit_cost || 0}
                  onChange={(e) => setFormData({ ...formData, unit_cost: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Preço de custo do item (se não informado, será igual ao preço de venda)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Preço de Venda Unitário *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  required
                  value={formData.unit_price}
                  onChange={(e) => setFormData({ ...formData, unit_price: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="0.00"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Preço que será repassado ao cliente
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Total
                </label>
                <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg">
                  {projectBudgetService.formatCurrency((formData.quantity || 0) * (formData.unit_price || 0))}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descrição
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Descrição detalhada do item..."
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                <Save className="w-4 h-4 mr-2" />
                {editingItem ? 'Atualizar' : 'Salvar'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Seção Financeira - Apenas para projetos aprovados */}
      {project && ['PLANNING', 'IN_PROGRESS', 'PRODUCTION', 'ASSEMBLY', 'COMPLETED', 'DELIVERED'].includes(project.status) && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
            <div className="flex items-center gap-3">
              <CreditCard className="w-6 h-6 text-green-600" />
              <h2 className="text-lg font-semibold text-gray-900">Financeiro do Projeto</h2>
            </div>
            <span className="text-xs text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200">
              Pagamentos gerenciados pelo Contrato / Saldo Inicial
            </span>
          </div>

          {/* Resumo Financeiro */}
          {(() => {
            const totalContractFromIncomes = expectedIncomes.reduce((sum, inc) => {
              const val = parseFloat(inc.original_amount || inc.expected_amount || 0);
              return sum + val;
            }, 0);

            const effectiveBudget = parseFloat(project.estimated_budget || 0) > 0
              ? parseFloat(project.estimated_budget)
              : totalContractFromIncomes;

            const paymentsTotal = payments.reduce((sum, p) => sum + parseFloat(p.amount || 0), 0);
            const pendingTotal = expectedIncomes.reduce((sum, inc) => {
              if (inc.status === 'PENDING' || inc.status === 'PARTIAL') {
                return sum + parseFloat(inc.expected_amount || 0);
              }
              return sum;
            }, 0);

            const effectiveReceived = paymentsTotal;
            const effectivePending = pendingTotal > 0 ? pendingTotal : Math.max(0, effectiveBudget - effectiveReceived);

            return (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="text-sm text-gray-600 mb-1">Orçamento Aprovado</div>
                  <div className="text-2xl font-bold text-blue-600">
                    {projectsService.formatCurrency(effectiveBudget)}
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="text-sm text-gray-600 mb-1">Total Recebido</div>
                  <div className="text-2xl font-bold text-green-600">
                    {projectFinanceService.formatCurrency(effectiveReceived)}
                  </div>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                  <div className="text-sm text-gray-600 mb-1">Pendente</div>
                  <div className="text-2xl font-bold text-orange-600">
                    {projectFinanceService.formatCurrency(effectivePending)}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Condição de Pagamento Selecionada */}
          {(project.selected_payment_condition_id || project.payment_condition_name) && (
            <div className="bg-purple-50 p-4 rounded-lg border border-purple-200 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Condição de Pagamento Escolhida</div>
                  <div className="text-lg font-semibold text-purple-700">
                    {project.payment_condition_name || 'Personalizada'}
                    {project.payment_condition_installments > 1 && (
                      <span className="text-sm text-purple-500 ml-2">
                        ({project.payment_condition_installments}x
                        {project.payment_condition_has_interest ? ' com juros' : ' sem juros'})
                      </span>
                    )}
                  </div>
                </div>
                <CreditCard className="w-6 h-6 text-purple-500" />
              </div>
            </div>
          )}

          {/* Lista de Pagamentos Recebidos */}
          {payments.length > 0 && (
            <div className="mb-6">
              <h3 className="text-md font-semibold mb-3 text-gray-900">Pagamentos Recebidos ({payments.length})</h3>
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-4 py-2 text-left">Data</th>
                      <th className="px-4 py-2 text-left">Valor</th>
                      <th className="px-4 py-2 text-left">Forma de Pagamento</th>
                      <th className="px-4 py-2 text-left">Descrição</th>
                      <th className="px-4 py-2 text-center">Origem</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {payments.map((payment) => (
                      <tr key={`${payment.payment_source || 'pp'}-${payment.id}`} className="hover:bg-gray-50">
                        <td className="px-4 py-2 whitespace-nowrap text-gray-700">
                          {new Date(payment.payment_date).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="px-4 py-2 font-bold text-emerald-600 whitespace-nowrap">
                          {projectFinanceService.formatCurrency(payment.amount)}
                        </td>
                        <td className="px-4 py-2 whitespace-nowrap text-gray-700 font-medium">
                          {payment.payment_method_name || '-'}
                        </td>
                        <td className="px-4 py-2 text-gray-700">
                          {payment.description || '-'}
                        </td>
                        <td className="px-4 py-2 text-center whitespace-nowrap">
                          <span className="inline-flex px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                            {payment.payment_source === 'income' ? 'Contrato / Saldo Inicial' : 'Lançamento Obra'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Entradas Previstas */}
          {expectedIncomes.length > 0 && (
            <div>
              <h3 className="text-md font-semibold mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-blue-600" />
                Entradas Previstas
              </h3>
              <div className="space-y-2">
                {expectedIncomes.map((income) => {
                  const isPaid = income.status === 'COMPLETED' || income.status === 'RECEIVED';
                  const isPartial = income.status === 'PARTIAL';
                  const faceAmount = parseFloat(income.original_amount || 0) > 0
                    ? parseFloat(income.original_amount)
                    : (parseFloat(income.amount_received || 0) > 0 ? parseFloat(income.amount_received) : parseFloat(income.expected_amount || 0));

                  const displayAmount = isPaid
                    ? faceAmount
                    : (parseFloat(income.expected_amount || 0) > 0 ? parseFloat(income.expected_amount) : faceAmount);

                  return (
                    <div key={income.id} className={`p-3 rounded-lg border ${isPaid ? 'bg-emerald-50/80 border-emerald-200' : isPartial ? 'bg-amber-50/80 border-amber-200' : 'bg-blue-50 border-blue-200'}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <div className={`font-semibold ${isPaid ? 'text-emerald-950 font-bold' : 'text-blue-900'}`}>
                            {projectFinanceService.formatCurrency(displayAmount)}
                          </div>
                          <div className="text-sm text-gray-600">
                            {income.expected_date 
                              ? `Previsto para: ${new Date(income.expected_date).toLocaleDateString('pt-BR')}`
                              : 'Sem data prevista'}
                          </div>
                          {income.description && (
                            <div className="text-sm text-gray-500 mt-1">{income.description}</div>
                          )}
                        </div>
                        <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                          isPaid ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' :
                          isPartial ? 'bg-amber-100 text-amber-800 border border-amber-300' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {isPaid ? '✓ Recebido' :
                           isPartial ? 'Parcial' :
                           'Pendente'}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Lista de itens */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Itens do Orçamento</h2>
            <div className="flex items-center text-lg font-bold text-green-600">
              <DollarSign className="w-5 h-5 mr-2" />
              Total: {projectBudgetService.formatCurrency(total)}
            </div>
          </div>
        </div>

        {budgetItems.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <DollarSign className="w-12 h-12 mx-auto mb-4 text-gray-400" />
            <p>Nenhum item de orçamento cadastrado ainda.</p>
            <p className="text-sm mt-2">Clique em "Adicionar Item" para começar.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Item
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Categoria
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Quantidade
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Preço Unit.
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {budgetItems.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{item.item_name}</div>
                      {item.description && (
                        <div className="text-sm text-gray-500">{item.description}</div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                        {categories.find(c => c.value === item.category)?.label || item.category || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {item.quantity} {item.unit}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {projectBudgetService.formatCurrency(item.unit_price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {projectBudgetService.formatCurrency(item.total_price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEdit(item)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50">
                <tr>
                  <td colSpan="4" className="px-6 py-4 text-right text-sm font-medium text-gray-900">
                    Total Geral:
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                    {projectBudgetService.formatCurrency(total)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* Modal de Importação de Cortes PDF */}
      {showCortesPdfModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <CortesPdfToBudget
            projectId={projectId}
            onClose={() => setShowCortesPdfModal(false)}
            onItemAdded={() => {
              loadBudgetItems();
            }}
          />
        </div>
      )}

      {/* Confirm Modal for Build Budget */}
      <ConfirmModal
        show={showBuildBudgetConfirm}
        onConfirm={confirmBuildBudget}
        onCancel={() => setShowBuildBudgetConfirm(false)}
        title="Confirmar Montagem do Orçamento"
        message={buildBudgetConfirmMessage}
        confirmText="Montar Orçamento"
        cancelText="Cancelar"
        type="warning"
      />

      {/* Confirm Modal for Delete Item */}
      <ConfirmModal
        show={showDeleteItemConfirm}
        onConfirm={confirmDeleteItem}
        onCancel={() => {
          setShowDeleteItemConfirm(false);
          setDeleteItemId(null);
        }}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este item?"
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />

      {/* Confirm Modal for Delete Payment */}
      <ConfirmModal
        show={showDeletePaymentConfirm}
        onConfirm={confirmDeletePayment}
        onCancel={() => {
          setShowDeletePaymentConfirm(false);
          setDeletePaymentId(null);
        }}
        title="Confirmar Exclusão"
        message="Tem certeza que deseja excluir este pagamento?"
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default ProjectBudget;
